import {
    defineStore,
} from 'pinia'
import {
    useLogStore,
} from '~~/stores/log'
import {
    useBitwardenStore,
} from '~~/stores/bitwarden'
import {
    useContextStore,
} from '~~/stores/context'
import { PROVIDERS } from '#shared/providers'

export const usePloiStore = defineStore('ploi', () => {
    const PROVIDER = PROVIDERS.PLOI

    const servers = ref([])
    const sites = ref([])
    const databases = ref([])
    const selectedDatabases = ref([])
    const databaseUsers = ref([])
    const databaseBackups = ref([])
    const allDatabaseBackups = ref([])
    const scripts = ref([])
    const deploymentScript = ref('')

    const getDatabaseServer = (database) => servers.value.find(server => server.id === database.server_id)

    const contextStore = useContextStore()
    contextStore.registerProvider(PROVIDER)

    const ctx = computed(() => contextStore.getContext(PROVIDER))
    watch(ctx, (newCtx, oldCtx) => {
        if (newCtx?.identityKey !== oldCtx?.identityKey) {
            servers.value = []
            sites.value = []
            databases.value = []
            scripts.value = []
        }
    })

    function init() {}

    async function fetchServers() {
        useRequest({
            label: 'fetch ploi servers',
            fetch: () => $fetch('/api/ploi/servers', {
                method: 'GET',
            }),
            evaluate: ({ servers: s }) => [...s],
            store: (s) => servers.value = s,
            successDetails: (s) => [s.length],
        })
    }

    async function fetchSites() {
        useRequest({
            label: 'fetch ploi sites',
            fetch: () => $fetch('/api/ploi/sites', {
                method: 'GET',
            }),
            evaluate: ({ sites: s }) => [...s],
            store: (s) => sites.value = s,
            successDetails: (s) => [s.length],
        })
    }

    async function createSite({ serverId, domain, systemUserName, bitwardenCollectionId }) {
        const user = await useRequest({
            label: `create system user ${systemUserName} for ploi site`,
            fetch: () => $fetch(`/api/ploi/servers/${serverId}/users`, {
                method: 'POST',
                body: { domain, systemUserName },
            }),
        })

        await createBitwardenItemForSystemUser({
            serverId,
            username: systemUserName,
            password: user.password,
            collectionId: bitwardenCollectionId,
            servers: servers.value,
        })

        await useRequest({
            label: `create ploi site ${domain} on system user ${systemUserName}`,
            fetch: () => $fetch(`/api/ploi/servers/${serverId}/sites`, {
                method: 'POST',
                body: { domain, systemUserName },
            }),
        })
    }

    async function fetchDatabases() {
        useRequest({
            label: 'fetch ploi databases',
            fetch: () => $fetch('/api/ploi/databases', {
                method: 'GET',
            }),
            evaluate: ({ databases: d }) => [...d],
            store: (d) => databases.value = d,
            successDetails: (d) => d.length,
        })
    }

    async function fetchDatabaseUsers(databaseId) {
        const database = databases.value.find(db => db.id === databaseId)
        useRequest({
            label: `fetch ploi database users for ${databaseId}`,
            fetch: () => $fetch(`/api/ploi/databases/${databaseId}/users`, {
                method: 'GET',
                params: { server_id: database.server_id },
            }),
            evaluate: ({ users }) => [...users],
            store: (users) => databaseUsers.value = users,
            successDetails: (users) => users?.length || 'n/a',
        })
    }

    async function fetchDatabaseBackups(databaseId) {
        const database = databases.value.find(db => db.id === databaseId)
        useRequest({
            label: `fetching ploi database backups for ${databaseId}`,
            fetch: () => $fetch(`/api/ploi/databases/${databaseId}/backups`, {
                method: 'GET',
                params: { server_id: database.server_id },
            }),
            evaluate: ({ backups }) => [...backups],
            store: (backups) => {
                databaseBackups.value = [
                    backups?.map((backup) => ({
                        id: backup.id,
                        type: backup.type,
                        path: backup.path,
                        active: backup.active,
                        last_backup_at: backup.last_backup_at,
                        created_at: backup.created_at,
                        databaseName: database.name,
                        serverId: database.server_id,
                    })),
                ]
            },
            successDetails: (backups) => backups?.length || 'n/a',
        })
    }

    async function fetchAllDatabaseBackups() {
        useRequest({
            label: 'fetch all ploi database backups',
            fetch: () => $fetch(`/api/ploi/databases/backups`, {
                method: 'GET',
            }),
            evaluate: ({ backups }) => [...backups],
            store: (backups) => allDatabaseBackups.value = backups,
            successDetails: (backups) => backups?.length || 'n/a',
        })
    }

    async function createDatabase({ serverId, name, rootUserName, rootUserPassword, bitwardenCollectionId }) {
        const database = await useRequest({
            label: 'create a ploi database',
            fetch: () => $fetch('/api/ploi/databases', {
                method: 'POST',
                body: { serverId, name, rootUserName, rootUserPassword },
            }),
            evaluate: ({ database: d }) => d,
            store: (d) => databases.value.push(d),
            successDetails: (d) => [`${d.id} ${d.name}`],
        })

        await createBitwardenItemForDatabaseUser({
            serverId,
            database,
            username: rootUserName,
            password: rootUserPassword,
            collectionId: bitwardenCollectionId,
            servers: servers.value,
        })
    }

    async function createDatabaseUser({ database, username, password, remote, bitwardenCollectionId }) {
        const { server_id } = database
        await useRequest({
            label: 'create a ploi database user',
            fetch: () => $fetch(`/api/ploi/databases/${database.id}/users`, {
                method: 'POST',
                params: { server_id },
                body: { username, password, remote },
            }),
            evaluate: ({ user }) => user,
            successDetails: (user) => [`${user?.user || 'n/a'} in database ${database?.name || 'n/a'}`],
        })

        await createBitwardenItemForDatabaseUser({
            serverId: server_id,
            database,
            username,
            password,
            collectionId: bitwardenCollectionId,
            servers: servers.value,
        })
    }

    async function fetchScripts() {
        useRequest({
            label: 'fetch ploi scripts',
            fetch: () => $fetch('/api/ploi/scripts', {
                method: 'GET',
            }),
            evaluate: ({ scripts: s }) => [...s],
            store: (s) => scripts.value = s,
            successDetails: (s) => s.length,
        })
    }

    async function createScript({ label, user, code }) {
        useRequest({
            label: 'create a ploi script',
            fetch: () => $fetch(`/api/ploi/scripts`, {
                method: 'POST',
                body: { label, user, code },
            }),
        })
    }

    async function runScript({ scriptId, serverId }) {
        useRequest({
            label: `run a ploi script`,
            fetch: () => $fetch(`/api/ploi/scripts/${scriptId}/run`, {
                method: 'POST',
                body: { servers: [serverId] },
            }),
        })
    }

    async function createCertificate({ siteId }) {
        const site = sites.value.find(s => s.id === siteId)
        useRequest({
            label: `create a certificate for ploi site ${siteId}`,
            fetch: () => $fetch(`/api/ploi/servers/${site.server_id}/sites/${siteId}/certificates`, {
                method: 'POST',
            }),
            evaluate: (data) => data.result.message,
            successDetails: (message) => message,
        })
    }

    async function deploySite({ siteId }) {
        const site = sites.value.find(s => s.id === siteId)
        useRequest({
            label: `deploying a ploi site ${siteId}`,
            fetch: () => $fetch(`/api/ploi/servers/${site.server_id}/sites/${siteId}/deploy`, {
                method: 'POST',
            }),
            evaluate: ({ data }) => data.result.message,
            successDetails: (message) => message,
        })
    }

    async function fetchDeploymentScript({ siteId }) {
        const site = sites.value.find(s => s.id === siteId)
        useRequest({
            label: `fetch deployment script for ploi site ${siteId}`,
            fetch: () => $fetch(`/api/ploi/servers/${site.server_id}/sites/${siteId}/deployment-script`, {
                method: 'GET',
            }),
            evaluate: (data) => data.result.deploy_script,
            store: (script) => deploymentScript.value = script,
        })
    }

    async function updateDeployScript({ siteId, script }) {
        const site = sites.value.find(s => s.id === siteId)
        useRequest({
            label: `updating deployment script for ploi site ${siteId}`,
            fetch: () => $fetch(`/api/ploi/servers/${site.server_id}/sites/${siteId}/deployment-script`, {
                method: 'PUT',
                body: { script },
            }),
        })
    }

    return {
        servers,
        sites,
        databases,
        selectedDatabases,
        databaseUsers,
        databaseBackups,
        allDatabaseBackups,
        scripts,
        deploymentScript,
        getDatabaseServer,
        init,
        fetchServers,
        fetchSites,
        createSite,
        fetchDatabases,
        fetchDatabaseUsers,
        fetchDatabaseBackups,
        fetchAllDatabaseBackups,
        createDatabase,
        createDatabaseUser,
        fetchScripts,
        createScript,
        runScript,
        createCertificate,
        deploySite,
        fetchDeploymentScript,
        updateDeployScript,
    }
})

async function createBitwardenItemForSystemUser({ serverId, username, password, collectionId, servers }) {
    const server = servers.find(server => server.id === serverId)

    await createBitwardenItem({
        name: `${server.name} server on ploi`,
        uri: `https://ploi.io/panel/servers/${serverId}/manage`,
        username,
        password,
        collectionId,
        ipAddress: server.ip_address,
    })

    const logStore = useLogStore()
    logStore.info(`added user credentials for ploi system user ${username || 'n/a'} to bitwarden`)
}

async function createBitwardenItemForDatabaseUser({ serverId, database, username, password, collectionId, servers }) {
    const server = servers.find(server => server.id === serverId)

    await createBitwardenItem({
        name: `${database.name} db on ploi`,
        uri: `https://ploi.io/panel/servers/${serverId}/databases`,
        username,
        password,
        collectionId,
        ipAddress: server.ip_address,
    })

    const logStore = useLogStore()
    logStore.info(`added user credentials for ploi database user ${username || 'n/a'} in database ${database?.name || 'n/a'} to bitwarden`)
}

async function createBitwardenItem({ name, uri, username, password, collectionId, ipAddress }) {
    const bitwardenStore = useBitwardenStore()
    await bitwardenStore.createItem({
        name,
        username,
        password,
        collectionId,
        fields: {
            'Server IP': { value: ipAddress, type: 0 },
        },
        uris: [{ match: 0, uri }],
    })
}
